/**
 * `<TaskNodeCard/>` — one node card on the canvas.
 *
 * Issue #759 (Phase 2). The card is the visual heart of B2.1/B2.2/B8.3:
 *   - status chip (top-left), title (bold), assignee avatars (right),
 *     a note glyph when the node carries a description, and — for parents —
 *     a slim computed roll-up bar + "N of M done" chip (about the WORK,
 *     never a person; §A6/§A9).
 *   - B2.2: when focused, the card shows inline quick-actions — a 4-dot
 *     state swatch row (PATCH state), then an action bar with `+ sub-task`,
 *     `@ assign`, a `link →` cue (only when the `task_dependencies` layer is
 *     on — it points at the drag-to-link handle, #896), and a `⋯` overflow
 *     (Open details / Rename / Archive). Inline rename happens on the card.
 *     The card is sized (layout.nodeWidth) so neither row spills (#894).
 *   - B8.3: a node with a collapsed subtree shows a "▶ N tasks" chip.
 *
 * All copy is factual + calm. Colours come from `state.ts` + `palette.ts`
 * so the card matches the gallery. The card is presentational: every
 * action is a callback the editor wires to a mutation.
 */

import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { formatRelativeShort } from "../history/formatTime";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import { useTeamZone } from "../theme/TeamZoneContext";
import type { TreeNode } from "../tree/buildTree";
import { formatEstimate } from "../tree/estimates";
import { TASK_STATES, type TaskState } from "../tree/state";
import { AssignPopover } from "./AssignPopover";
import { AvatarRow } from "./Avatar";
import { ChangeStateMenu } from "./ChangeStateMenu";
import { StatusChip } from "./StatusChip";
import { timingMeta } from "./taskTimingMeta";

export interface TaskNodeCardProps {
  node: TreeNode;
  members: TaskMember[];
  /** Whether this card is the focused node (B2.2). */
  focused: boolean;
  /** Whether some OTHER node is focused (this one dims). */
  dimmed: boolean;
  /**
   * "My parts" highlight (B5.2, member full-map). When true the card gets the
   * indigo emphasis border + a small "you" pill, marking it as part of the
   * member's own slice (collaborative sight, never surveillance — §A8/§A9).
   * Optional + defaults off, so the lead editor surface is unchanged.
   */
  highlighted?: boolean;
  /** Whether this node's subtree is collapsed (shows the "▶ N" chip). */
  collapsed: boolean;
  /**
   * Compact "between / connector" treatment (#1043, insert-on-link fix). When
   * true the card renders as a SMALL bead sitting on a dependency chain
   * (`A ──▶ new ──▶ B`) — reduced padding, a smaller title, a tighter top row —
   * rather than a rigid full-size tree-sibling. The state chip + assignees stay
   * legible. Purely structural/visual correctness, so it applies in BOTH theme
   * modes. Optional + defaults off, so a normal node renders byte-identically.
   */
  compact?: boolean;
  /**
   * Zoom-out title legibility counter-scale (#1043). The whole canvas plane is
   * `scale(zoom)`, so titles shrink when zoomed out. The canvas passes a scale
   * factor (> 1 when zoomed out, exactly 1 at zoom ≥ 1) that we apply to the
   * TITLE element only — never the whole card — so the on-screen title size
   * stays readable while the layout still scales. Optional + defaults to 1, so
   * a standalone render (or a card at zoom 1) is unchanged.
   */
  titleScale?: number;
  /**
   * Read-only mode (B3.3 rewind). When true the card renders the node's
   * resting appearance only — it never reveals the focus quick-actions
   * (state swatches / rename / archive / assign), and clicking it does not
   * focus-to-edit. The card is a faithful, inert picture of the node as it
   * stood. Defaults off, so the live editor surface is unchanged.
   */
  readOnly?: boolean;
  /**
   * Whether the `task_dependencies` layer (#766) is on for this team. When on,
   * the focused card surfaces a `link →` cue that points at the drag-to-link
   * handle (the real gesture lives on the canvas, §A7); when off, the card
   * shows no link affordance at all (we never paint a permanently-disabled,
   * misleading control — #896). Defaults off, so a team without the layer sees
   * the card unchanged.
   */
  dependenciesEnabled?: boolean;
  /**
   * Whether the `task_estimates` layer (B2.12, #1043) is on for this team. When
   * on AND the node carries an estimate AND editorial, the top row shows a calm
   * coarse `~Xd` mono meta next to the state chip (the wireframe `V_B212`).
   * Display-only, about the WORK, never a contract or a per-person commitment
   * (§A9). Defaults off, so a team without the layer sees the card unchanged.
   */
  estimatesEnabled?: boolean;
  /**
   * Whether the `task_notes` layer (#767) is on for this team (#1043, item 8).
   * When on AND the node carries a `lastNoteAt`, the card shows a calm
   * "🗒 <relative>" recency cue so a viewer sees at a glance that the task's
   * notes were recently touched. A factual recency cue about the WORK's
   * collaboration thread, never a tally of a person (§A9). Defaults to showing
   * whenever `lastNoteAt` is non-null (so a standalone render still works).
   */
  notesEnabled?: boolean;
  /**
   * Whether the viewer may AUTHOR this node (#1043, items 5/6/7). Gates the
   * always-on affordances — the quick-add-subtask "+", the assign trigger on
   * the avatars, and click-the-title inline rename — to the same recursive
   * authorship rule the focused quick-actions already obey (`canAuthor`,
   * §A5). Optional: when omitted (the lead editor, where every card is
   * authorable) the affordances show; pass `false` (a non-author on the member
   * full-map) to suppress them. The backend remains the enforcer.
   */
  canAuthor?: boolean;
  /**
   * Click-to-link state (#904). `linkArmed` = this card IS the armed link
   * source (it shows an "armed" emphasis + its `link →` button reads as the
   * active/cancel control). `linkPickTarget` = SOME OTHER card is armed, so
   * this card is a pickable target (a subtle "click to link" affordance). Both
   * default off; only meaningful when `dependenciesEnabled`.
   */
  linkArmed?: boolean;
  linkPickTarget?: boolean;
  /**
   * Arm click-to-link from this card (fired by the `link →` button). Only wired
   * when the dependency layer is on; `undefined` otherwise (the button falls
   * back to a plain focus).
   */
  onArmLink?: (() => void) | undefined;
  /**
   * The multi-outcome root badge (#1043, B2.14) — a small mono "OUTCOME N"
   * chip on a root card, shown only when the map holds 2+ outcomes so each
   * root reads as a distinct outcome (the wireframe's `rootBadge`). The canvas
   * passes the ordinal label (e.g. "OUTCOME 2"); omitted (single-outcome team,
   * legacy theme, or a non-root card) ⇒ no chip, byte-unchanged.
   */
  outcomeBadge?: string | undefined;
  onSelect: () => void;
  onToggleCollapse: () => void;
  onSetState: (state: TaskState) => void;
  onAddSubtask: () => void;
  onRename: (title: string) => void;
  onArchive: () => void;
  onAssignToggle: (membershipId: string, assign: boolean) => void;
  /** Open this node's detail drawer (B2.5) — double-click or the "Details" action. */
  onOpenDrawer: () => void;
  /**
   * Persist a Blocked reason typed in this card's change-state menu as a note
   * (#953), mirroring the drawer's `onSaveReason` seam. Supplied only when the
   * `task_notes` layer is on; when omitted the reason step degrades to its calm
   * placeholder (commit Blocked, no note) — exactly as the drawer does off.
   */
  onSaveBlockedReason?: ((reason: string) => void) | undefined;
  /**
   * Round-4 ⑥ (#1043) — open the overflow's "Move under…" picker. The card
   * surfaces the menu item; the parent supplies the actual handler (it knows
   * the eligible-candidate list and the cycle/permission guards). Hidden for
   * `link_block` cards (they're off-tree and never get a parent). Omit to
   * suppress the menu item entirely (read-only / member view).
   */
  onMoveUnder?: (() => void) | undefined;
  /**
   * Round-4 ⑥ (#1043) — reset this card back to tidy-tree placement (clears
   * its pinned `layoutX/layoutY`). Visible only when the card IS pinned;
   * fires the parent's `useSetNodePosition({x: null, y: null})` call. Omit
   * to suppress (read-only).
   */
  onResetAutoLayout?: (() => void) | undefined;
}

function RollupBar({ done, total }: { done: number; total: number }) {
  const c = useMapTheme().palette;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2" data-testid="rollup">
      <div
        className="h-1 flex-1 overflow-hidden rounded-full"
        style={{ backgroundColor: c.rollupTrack }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: c.rollupFill }}
        />
      </div>
      <span
        className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none"
        style={{ backgroundColor: c.rollupBg, color: c.rollupInk }}
      >
        {done} of {total} done
      </span>
    </div>
  );
}

export function TaskNodeCard(props: TaskNodeCardProps) {
  const {
    node,
    members,
    focused,
    dimmed,
    highlighted = false,
    collapsed,
    compact = false,
    titleScale = 1,
    readOnly = false,
    dependenciesEnabled = false,
    estimatesEnabled = false,
    notesEnabled = false,
    canAuthor = true,
    linkArmed = false,
    linkPickTarget = false,
    onArmLink,
    onSelect,
    onToggleCollapse,
    onSetState,
    onAddSubtask,
    onRename,
    onArchive,
    onAssignToggle,
    outcomeBadge,
    onOpenDrawer,
    onSaveBlockedReason,
    onMoveUnder,
    onResetAutoLayout,
  } = props;

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const teamZone = useTeamZone();
  const timingText = timingMeta(node, teamZone);

  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(node.title);
  const [overflowOpen, setOverflowOpen] = useState(false);
  // Editorial "Delete" (#1043): a one-step confirm lives inside the overflow
  // menu so a tap never archives by accident. Reset whenever the menu closes.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Item 6 (#1043): the assign popover anchored on the avatars cluster, opened
  // by clicking the avatars / the empty-state "assign" chip. Separate from the
  // focused action-row `pickerOpen` so the avatars trigger works even before a
  // card is focused (clicking the avatars focuses + opens in one gesture).
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);
  // Round-4 item ② (#1043) — mousing over a card auto-mini-expands it (shows
  // the B2.2 focused quick-actions block), so a user gets at the inline state
  // swatches + `+ sub-task` / `@ assign` / `link →` / `⋯` without first having
  // to click to focus. Tap/touch is unchanged: a tap fires onClick → focus →
  // the same block via `focused`. `quickActionsActive` (below) ORs in any
  // currently-open in-row menu (state / assign / overflow) so moving the cursor
  // from the card down to an absolutely-positioned popover doesn't dismiss it.
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Round-4 Bug C (#1043, regression from #1116) — the hover quick-actions block
  // paints below the card's fixed-height layout box, so a cursor crossing the
  // visual gap from card body to a painted-below button briefly exits the card's
  // hit region and fires `mouseleave`. We defer the hover-off flip by a short
  // grace window so the cursor can land on a child button before `hovered →
  // false` dismounts the block. Cancel on re-enter; clear on unmount.
  const hoverOffTimeout = useRef<number | null>(null);

  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  // Cancel any pending hover-off timeout when the card unmounts mid-grace, so we
  // never call setHovered on an unmounted component.
  useEffect(() => {
    return () => {
      if (hoverOffTimeout.current !== null) {
        window.clearTimeout(hoverOffTimeout.current);
        hoverOffTimeout.current = null;
      }
    };
  }, []);

  const assignees = node.assigneeIds
    .map((id) => members.find((m) => m.membershipId === id))
    .filter((m): m is TaskMember => Boolean(m));
  const hasChildren = node.children.length > 0;
  const isParent = hasChildren;
  // Editorial root node (the OUTCOME): an ink-filled card with cream text
  // (`tarii.css` `.t-node.root`). Roots carry no parent; legacy never styled
  // roots distinctly, so this is editorial-only.
  const isRoot = editorial && node.parentId === null;

  // B8.2: a blocked node renders distinctly but **calmly**, never alarm red.
  //   - LEGACY: soft amber fill + amber border (gallery #FFFBF5 on #D97706).
  //   - EDITORIAL: the calm surface stays; the amber state colours carry the
  //     fact via the chip/dot + a soft amber border ring (the wireframe keeps
  //     the node card itself calm — state lives in the chip).
  const blocked = node.state === "blocked";
  const LEGACY_BLOCKED_BG = "#FFFBF5";
  const LEGACY_BLOCKED_BORDER = "#D97706";
  const editorialBlockedBorder = theme.stateStyle("blocked").dot ?? c.rule2;
  // B5.2 "my parts" emphasis. LEGACY = the gallery's indigo (#4F46E5) "lit"
  // card; EDITORIAL = the wireframe `.t-node.lit` (warm-cream fill #FFFEF8 +
  // ink border + soft amber shadow).
  const LEGACY_HIGHLIGHT_BORDER = "#4F46E5";
  const EDITORIAL_LIT_BG = "#FFFEF8";
  // The dependency teal (#904) — matches the connector port + arrows, so the
  // armed source + the pickable targets read as one gesture. Editorial uses
  // the warm teal accent; legacy keeps the gallery dependency hue.
  const linkTeal = editorial ? c.teal : "#0D9488";
  // Resting surface + border.
  const cardBg = isRoot
    ? c.ink
    : highlighted && editorial
      ? EDITORIAL_LIT_BG
      : blocked && !editorial
        ? LEGACY_BLOCKED_BG
        : editorial
          ? c.cardBg
          : "#FFFFFF";
  const restingBorder = blocked
    ? editorial
      ? editorialBlockedBorder
      : LEGACY_BLOCKED_BORDER
    : isRoot
      ? c.ink
      : editorial
        ? c.rule2
        : c.cardBorder;
  // Focus wins over highlight over the resting border. Editorial focus + lit
  // both use the ink border (`tarii.css` `.t-node.focused`/`.lit`); legacy
  // keeps the teal focus + indigo highlight. While click-to-link is active the
  // armed source + the pickable targets get the dependency ring.
  const editorialEmphasisBorder = editorial ? c.ink : c.teal;
  const focusBorder = linkArmed
    ? linkTeal
    : linkPickTarget
      ? linkTeal
      : focused
        ? editorialEmphasisBorder
        : highlighted
          ? editorial
            ? c.ink
            : LEGACY_HIGHLIGHT_BORDER
          : restingBorder;
  const emphasised = focused || blocked || highlighted || linkArmed || linkPickTarget;
  // Resting / emphasis shadow.
  //   - EDITORIAL resting: `0 1px 2px rgba(0,0,0,0.03)` (`.t-node`); focused:
  //     `0 4px 12px rgba(0,0,0,0.08)` (`.t-node.focused`); lit: a soft amber
  //     glow (`.t-node.lit`); blocked: keep it calm (resting shadow).
  //   - LEGACY: unchanged.
  const focusShadow = editorial
    ? highlighted
      ? "0 2px 8px rgba(201,133,45,0.12)"
      : "0 1px 2px rgba(0,0,0,0.03)"
    : blocked
      ? "0 2px 6px rgba(217,118,6,0.18)"
      : "0 1px 4px rgba(12,42,42,0.07)";

  const commitRename = () => {
    const next = draft.trim();
    if (next && next !== node.title) onRename(next);
    setRenaming(false);
  };

  // B2.12 — the card's coarse estimate meta (`~3d`), shown ONLY when the layer
  // is on, the node carries an estimate, and we're in editorial mode (the
  // wireframe surface). Display-only; about the WORK, never a contract (§A9).
  const estimateLabel = estimatesEnabled && editorial ? formatEstimate(node.estimateMinutes) : null;

  // The always-on card affordances (#1043, items 5/6/7) are shown only when the
  // card is live AND the viewer may author it — the same recursive authorship
  // gate the focused quick-actions obey (§A5). Read-only (B3.3 rewind) is an
  // inert picture, so it never gets them. The lead editor passes `canAuthor`
  // unset (defaults true), so its cards are unchanged.
  const mayAct = !readOnly && canAuthor;

  // Item 8 (#1043): the calm notes-recency cue. Shown when the node carries a
  // `lastNoteAt` AND (the notes layer is on OR the card has no notes-signal
  // wired — a standalone render). Display-only; about the WORK's thread, never
  // a person (§A9).
  const noteRecency = node.lastNoteAt ? formatRelativeShort(node.lastNoteAt) : null;
  const showNoteRecency = (notesEnabled || node.lastNoteAt != null) && noteRecency;

  // Round-4 item ③ (#1043) — shared title presentation. The read-mode <span>
  // and the rename <input> use the same className + font CSS so entering rename
  // feels like typing more letters into the existing words (no font / size /
  // weight / line-height / position shift). Editorial: 12.5px / weight 500
  // (root weight 600) / -0.005em / sans; legacy: text-sm or text-xs + 600 +
  // leading-snug. Colour respects `isRoot` (root cards paint title in canvas
  // cream against the inked root background).
  const titleClassName = editorial
    ? "leading-snug"
    : `${compact ? "text-xs" : "text-sm"} font-semibold leading-snug`;
  const titleFontStyle = {
    color: isRoot ? c.canvas : c.ink,
    ...(editorial
      ? {
          fontFamily: theme.fonts.sans,
          fontSize: compact ? "11px" : "12.5px",
          fontWeight: isRoot ? 600 : 500,
          letterSpacing: "-0.005em",
        }
      : {}),
  } as const;

  // Round-4 item ② (#1043) — show the focused B2.2 quick-actions block whenever
  // the card is focused OR hovered OR has an in-row menu open. The
  // menu-open clauses (`pickerOpen` = `@ assign`, `stateMenuOpen` = `✦`,
  // `overflowOpen` = `⋯`) keep the block mounted while the user moves the
  // cursor onto an absolutely-positioned popover that paints OUTSIDE the
  // card's box — otherwise the leave would dismiss the row mid-interaction.
  // Read-only (B3.3 rewind) always suppresses; tap/touch path is unchanged
  // (a click still focuses → block via `focused`).
  const quickActionsActive =
    !readOnly && (focused || hovered || pickerOpen || stateMenuOpen || overflowOpen);

  // Item 6 (#1043): clicking the avatars cluster (or the empty-state "assign"
  // chip) opens an AssignPopover anchored there. Gated on authorship; when the
  // viewer can't author, the avatars stay a plain non-interactive display.
  const openAvatarAssign = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (!mayAct) return;
    onSelect();
    setAvatarPickerOpen((v) => !v);
  };

  return (
    <div
      data-testid="task-node-card"
      data-node-id={node.id}
      data-focused={focused}
      data-readonly={readOnly || undefined}
      data-link-armed={linkArmed || undefined}
      data-link-pick={linkPickTarget || undefined}
      onClick={(e) => {
        e.stopPropagation();
        if (readOnly) return;
        onSelect();
      }}
      // Round-4 item ② (#1043) — hover auto-mini-expands (drives the focused
      // quick-actions block via `quickActionsActive`). Skipped read-only so the
      // B3.3 rewind canvas stays inert. Touch fires neither handler, so a tap
      // still focuses → the same block opens via `focused`. The hover-off flip
      // is deferred 180ms (Bug C grace window) so the cursor can cross the
      // visual gap from the card body to a painted-below quick-action button
      // without dismounting the block mid-traverse.
      onMouseEnter={() => {
        if (readOnly) return;
        if (hoverOffTimeout.current !== null) {
          window.clearTimeout(hoverOffTimeout.current);
          hoverOffTimeout.current = null;
        }
        setHovered(true);
      }}
      onMouseLeave={() => {
        if (readOnly) return;
        if (hoverOffTimeout.current !== null) {
          window.clearTimeout(hoverOffTimeout.current);
        }
        hoverOffTimeout.current = window.setTimeout(() => {
          hoverOffTimeout.current = null;
          setHovered(false);
        }, 180);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (readOnly) return;
        onOpenDrawer();
      }}
      onKeyDown={(e) => {
        if (readOnly) return;
        if (e.key === "Enter" && !renaming) onSelect();
      }}
      // biome-ignore lint/a11y/useSemanticElements: the card composes interactive children (inputs, buttons) so it cannot be a native <button>; role+tabIndex+key handler give it equivalent focus/activation semantics.
      role="button"
      tabIndex={0}
      // Read-only (B3.3 rewind): the card is an inert picture — it stays a
      // button-role element for consistent structure, but announces itself
      // disabled and ignores every activation (handlers early-return above).
      aria-disabled={readOnly || undefined}
      data-blocked={blocked}
      data-highlighted={highlighted || undefined}
      // #1043 — observable compact marker for the "between / connector" node.
      data-compact={compact || undefined}
      // Round-4 ⑥ (#1043) — `link_block` cards expose their kind so the
      // canvas + tests can spot off-tree annotations. `task` is the default
      // (no attribute), so existing assertions are byte-identical.
      data-kind={node.kind === "link_block" ? "link_block" : undefined}
      className={`relative flex flex-col rounded-lg transition-shadow ${
        // Compact connector: tighter gap + padding so the small bead doesn't
        // feel hollow; a normal card keeps its roomy gap-2 p-3.
        compact ? "gap-1 p-2" : "gap-2 p-3"
      } ${readOnly ? "" : "cursor-pointer"}`}
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: cardBg,
        borderRadius: editorial ? theme.radii.lg : undefined,
        // A pickable link target gets a dashed teal ring (clearly "click me to
        // link"); the armed source + every other emphasis is a solid ring.
        // Editorial focus/lit use a crisp 1.5px ink ring (`.t-node.focused`).
        // Round-4 ⑥ (#1043) — a `link_block` card carries a dashed border by
        // default so it visually reads as a freeform off-tree annotation. The
        // emphasis colour + width logic is unchanged; only the stroke style
        // shifts for link_blocks.
        border: editorial
          ? `${emphasised ? (linkPickTarget ? 2 : 1.5) : 1}px ${linkPickTarget || node.kind === "link_block" ? "dashed" : "solid"} ${focusBorder}`
          : `${emphasised ? 2 : 1}px ${linkPickTarget || node.kind === "link_block" ? "dashed" : "solid"} ${focusBorder}`,
        boxShadow: editorial
          ? focused
            ? "0 4px 12px rgba(0,0,0,0.08)"
            : focusShadow
          : focused
            ? "0 3px 9px rgba(31,122,110,0.16)"
            : focusShadow,
        // Editorial dim is the wireframe `.t-node.dimmed` (opacity .42); legacy
        // keeps its .55. While picking a target, keep candidates at full
        // opacity even if another node is focused — they're the actionable
        // surface now.
        opacity: dimmed && !linkPickTarget ? (editorial ? 0.42 : 0.55) : 1,
        cursor: linkPickTarget ? "alias" : undefined,
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      {/* Top row: status chip · estimate meta · note glyph · avatars */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <StatusChip state={node.state} />
          {/* B2.12 — coarse `~Xd` estimate, mono + faint, next to the chip. */}
          {estimateLabel && (
            <span
              data-testid="card-estimate"
              title="Estimate (coarse — not a contract)"
              style={{
                fontFamily: theme.fonts.mono,
                fontSize: 10.5,
                color: isRoot ? c.bgRecess : c.muted,
              }}
            >
              {estimateLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {/* Root badge (#1043, B2.14 + B8.4) — a quiet mono chip on a root card.
              `Outcome N` marks each root on the multi-outcome board; `✓ Completed`
              marks a COMPLETE outcome (every leaf done) — the canvas passes the
              label. The completed badge reads done-GREEN (a GOOD state) instead of
              the muted cream so it's unmistakably calm-positive; the multi-outcome
              chip keeps the quiet cream. Detected by the badge's leading `✓`, which
              the canvas owns. */}
          {isRoot && outcomeBadge && (
            <span
              data-testid="outcome-badge"
              data-complete={outcomeBadge.startsWith("✓") || undefined}
              className="shrink-0 leading-none"
              style={{
                fontFamily: theme.fonts.mono,
                fontSize: 9.5,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: outcomeBadge.startsWith("✓")
                  ? (theme.stateStyle("done").dot ?? theme.stateStyle("done").fg)
                  : c.bgRecess,
                opacity: outcomeBadge.startsWith("✓") ? 1 : 0.75,
              }}
            >
              {outcomeBadge}
            </span>
          )}
          {node.description && (
            <span
              data-testid="note-glyph"
              title="Has a note"
              aria-label="Has a note"
              style={{ color: isRoot ? c.bgRecess : c.muted }}
              className="text-xs leading-none"
            >
              💬
            </span>
          )}
          {/* Item 8 (#1043) — a calm "🗒 <relative>" notes-recency cue. Tells a
              viewer at a glance that this task's notes were recently touched;
              factual recency about the WORK's thread, never a tally OF a person
              (§A9). Muted, editorial palette. */}
          {showNoteRecency && (
            <span
              data-testid="note-recency"
              title={`Notes last updated ${formatRelativeShort(node.lastNoteAt as string)} ago`}
              aria-label={`Notes last updated ${formatRelativeShort(node.lastNoteAt as string)} ago`}
              className="inline-flex shrink-0 items-center gap-0.5 leading-none"
              style={{
                color: isRoot ? c.bgRecess : c.muted,
                fontSize: 10,
                fontFamily: editorial ? theme.fonts.mono : undefined,
              }}
            >
              <span aria-hidden>🗒</span>
              {noteRecency}
            </span>
          )}
          {/* Item 6 (#1043) — the assignee-avatars area IS the assign trigger.
              "who's assigned" and "assign someone" become the same place. When
              the viewer may author, clicking the cluster (or the empty-state
              "assign" chip) opens the AssignPopover anchored here; otherwise the
              avatars stay a plain, non-interactive display. Assignment is a
              neutral "who's on it", never a score or ranking (§A9). */}
          <div className="relative">
            {mayAct ? (
              assignees.length > 0 ? (
                <button
                  type="button"
                  data-testid="avatar-assign-trigger"
                  aria-label="Assign people"
                  aria-haspopup="dialog"
                  aria-expanded={avatarPickerOpen}
                  title="Who's on it — click to assign"
                  onClick={openAvatarAssign}
                  className="-m-0.5 rounded p-0.5 leading-none transition-colors hover:bg-black/5"
                >
                  <AvatarRow members={assignees} size={20} />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="avatar-assign-empty"
                  aria-label="Assign people"
                  aria-haspopup="dialog"
                  aria-expanded={avatarPickerOpen}
                  title="Assign someone"
                  onClick={openAvatarAssign}
                  className="inline-flex items-center gap-1 rounded-full border border-dashed px-1.5 py-0.5 text-[10px] leading-none transition-colors hover:bg-black/5"
                  style={{
                    borderColor: isRoot ? c.bgRecess : c.softBorder,
                    color: isRoot ? c.bgRecess : c.muted,
                    fontFamily: editorial ? theme.fonts.mono : undefined,
                  }}
                >
                  <span aria-hidden>+</span>
                  assign
                </button>
              )
            ) : (
              <AvatarRow members={assignees} size={20} />
            )}
            {avatarPickerOpen && mayAct && (
              <div
                className="absolute right-0 top-full z-30 mt-1"
                data-testid="avatar-assign-popover"
              >
                <AssignPopover
                  nodeTitle={node.title}
                  members={members}
                  assignedIds={node.assigneeIds}
                  onToggle={onAssignToggle}
                  onClose={() => setAvatarPickerOpen(false)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Item 5 (#1043) — an always-on quick-add-subtask "+" on EVERY card (not
          only the focused one). Subtle by default, emphasised on hover. Gated on
          authorship (`mayAct`) so a non-author on the member full-map never gets
          it; never shown read-only. Sits bottom-right so it clears the centred
          collapse toggle. Calm + factual; about the WORK. */}
      {mayAct && (
        <button
          type="button"
          data-testid="quick-add-subtask"
          aria-label="Add subtask"
          title="Add subtask"
          onClick={(e) => {
            e.stopPropagation();
            onAddSubtask();
          }}
          className="absolute -right-2 -bottom-2 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full border text-[12px] leading-none opacity-40 shadow-sm transition-opacity hover:opacity-100"
          style={{
            borderColor: editorial ? c.rule2 : c.softBorder,
            backgroundColor: editorial ? c.cardBg : "#FFFFFF",
            color: editorial ? c.teal : c.teal,
            fontFamily: editorial ? theme.fonts.sans : undefined,
          }}
        >
          +
        </button>
      )}

      {/* Title (inline-editable when renaming).

          Round-4 item ③ (#1043): the rename input is styled so editing feels
          like typing more letters into the existing words — same font / size /
          weight / letter-spacing / colour as the rendered title, transparent
          background, no visible border / padding / outline. The shared
          `titleClassName` + `titleFontStyle` keep both states identical, so
          there's no shift in size or vertical position when entering rename.
          And the click-to-rename trigger is the title TEXT itself (via an
          inline-block `<span>` sized to content + `self-start` on its layout
          slot), not the full-width row — clicking the empty space to the
          right of the title now hits the card (select), never rename. */}
      {renaming ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") {
              setDraft(node.title);
              setRenaming(false);
            }
          }}
          data-testid="rename-input"
          className={`w-full ${titleClassName}`}
          style={{
            ...titleFontStyle,
            // Look like text, not a box: transparent bg, no border / padding /
            // outline. Native caret stays visible (caret-color inherits).
            backgroundColor: "transparent",
            border: 0,
            outline: 0,
            padding: 0,
            margin: 0,
            appearance: "none" as const,
          }}
        />
      ) : (
        <div className="flex self-start">
          <span
            // Item 7 (#1043) — click the TITLE to inline-rename (no drawer). Only
            // when the viewer may author; `stopPropagation` keeps a title click
            // off the card's select/open-drawer handlers, so a title click never
            // opens the drawer while a click/double-click on the rest of the
            // card still selects / opens it. Reuses the existing
            // renaming/draft/onRename machinery (Enter / blur commit, Esc
            // cancel). Round-4 item ③ (#1043): an inline-block span sized to
            // the title text — the click target is the words themselves, not
            // the full-width row.
            {...(mayAct
              ? {
                  role: "button" as const,
                  tabIndex: 0,
                  "aria-label": `Rename “${node.title}”`,
                  title: "Click to rename",
                  onClick: (e: ReactMouseEvent) => {
                    e.stopPropagation();
                    setDraft(node.title);
                    setRenaming(true);
                  },
                  onKeyDown: (e: ReactKeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setDraft(node.title);
                      setRenaming(true);
                    }
                  },
                }
              : {})}
            className={`inline-block ${titleClassName}${mayAct ? " cursor-text hover:underline" : ""}`}
            style={{
              ...titleFontStyle,
              // #1043 — counter-scale the title when the canvas is zoomed out so
              // it stays legible. Grows from the LEFT edge so it stays anchored
              // to the card. Only set when actually counter-scaling (zoom < 1);
              // at scale 1 the resting render is byte-unchanged.
              ...(titleScale !== 1
                ? { transform: `scale(${titleScale})`, transformOrigin: "left center" }
                : {}),
            }}
            data-testid="node-title"
          >
            {node.title}
          </span>
        </div>
      )}

      {/* Start time and place of a timed task (#2705). */}
      {timingText ? (
        <span
          data-testid="card-timing"
          className="self-start text-[10px] font-semibold uppercase leading-none"
          style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.06em" }}
        >
          {timingText}
        </span>
      ) : null}

      {/* "you" pill (B5.2 — marks a card as part of the member's own slice). */}
      {highlighted && (
        <span
          data-testid="you-pill"
          className="self-start rounded px-1.5 py-0.5 text-[9px] font-bold leading-none"
          style={
            editorial
              ? {
                  backgroundColor: c.tealSoft,
                  color: c.tealInk,
                  fontFamily: theme.fonts.mono,
                  letterSpacing: "0.04em",
                }
              : { backgroundColor: "#EEF2FF", color: LEGACY_HIGHLIGHT_BORDER }
          }
        >
          you
        </span>
      )}

      {/* Parent roll-up (computed, display-only) */}
      {isParent && !collapsed && (
        <RollupBar done={node.doneDescendants} total={node.totalDescendants} />
      )}

      {/* B8.2: a calm, factual blocked row. A reason note is #767 — until then
          the row states the fact plainly (amber, never alarm). */}
      {blocked && (
        <div
          data-testid="blocked-row"
          className="flex items-center gap-1.5 rounded px-1.5 py-1 text-[10px]"
          style={
            editorial
              ? {
                  backgroundColor: theme.stateStyle("blocked").chipBg,
                  color: theme.stateStyle("blocked").fg,
                }
              : { backgroundColor: "#FEF3C7", color: "#92400E" }
          }
        >
          <span aria-hidden>💬</span>
          <span>Blocked</span>
        </div>
      )}

      {/* Collapsed-subtree chip (B8.3). Editorial = the wireframe
          `.t-node.collapsed-chip` (recessed fill, dashed border, muted ink). */}
      {collapsed && (
        <button
          type="button"
          data-testid="collapsed-chip"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          className={`self-start rounded border px-2 py-0.5 text-[11px] transition-colors hover:bg-slate-50 ${
            editorial ? "border-dashed" : ""
          }`}
          style={{
            borderColor: editorial ? c.rule2 : c.softBorder,
            color: editorial ? c.muted : c.slate,
            backgroundColor: editorial ? c.bgRecess : undefined,
            fontFamily: editorial ? theme.fonts.sans : undefined,
          }}
        >
          ▶ {node.totalDescendants} {node.totalDescendants === 1 ? "task" : "tasks"}
        </button>
      )}

      {/* Expand affordance when expanded + has children (B8.3) */}
      {hasChildren && !collapsed && (
        <button
          type="button"
          data-testid="collapse-toggle"
          aria-label="Collapse subtree"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full border bg-white px-1.5 text-[10px] leading-tight transition-colors hover:bg-slate-50"
          style={{ borderColor: c.softBorder, color: c.muted }}
        >
          ▾
        </button>
      )}

      {/* Inline quick-actions (B2.2) — active when the card is focused, hovered,
          or has an in-row menu open (round-4 item ②, #1043), and never in
          read-only mode (the B3.3 rewind canvas shows the node as it stood,
          with no edit affordances). The container has no onClick: each child
          action bubbles to the card's onSelect, idempotent on the focused node. */}
      {quickActionsActive && (
        <div
          className="mt-1 flex flex-col gap-2 border-t pt-2"
          style={{ borderColor: c.cardBorder }}
          data-testid="quick-actions"
        >
          {/* State swatch row — the quick inline change (B2.2). The "✦"
              caret opens the fuller B2.8 change-state menu (with the
              Blocked-reason step). The swatches own this row alone now (#894):
              `@ assign` moved to the action row below so nothing crowds — and
              spills past — the right edge. */}
          <div className="flex items-center gap-2">
            <span className="text-[10px]" style={{ color: c.muted }}>
              state:
            </span>
            {TASK_STATES.map((legacy) => {
              // Resolve each swatch's style through the active theme so the
              // editorial recolour (⚠ amber, cobalt/sage, distinct glyph) flows
              // through; `TASK_STATES` supplies only the lifecycle ORDER. The
              // standalone glyph uses the saturated `dot` colour (editorial),
              // falling back to `fg` (legacy — unchanged).
              const s = theme.stateStyle(legacy.state);
              const glyphColor = s.dot ?? s.fg;
              return (
                <button
                  key={s.state}
                  type="button"
                  aria-label={`Set state ${s.label}`}
                  data-testid={`set-state-${s.state}`}
                  onClick={() => onSetState(s.state)}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] leading-none transition-transform hover:scale-110"
                  style={{
                    color: glyphColor,
                    border:
                      node.state === s.state
                        ? `1.5px solid ${glyphColor}`
                        : "1px solid transparent",
                    backgroundColor: node.state === s.state ? s.chipBg : "transparent",
                  }}
                >
                  {s.glyph}
                </button>
              );
            })}
            <div className="relative ml-auto">
              <button
                type="button"
                aria-label="Change state"
                data-testid="change-state-button"
                onClick={() => setStateMenuOpen((v) => !v)}
                className="rounded border px-1 py-0.5 text-[11px] leading-none transition-colors hover:bg-slate-50"
                style={{ borderColor: c.softBorder, color: c.muted }}
              >
                ✦
              </button>
              {stateMenuOpen && (
                <ChangeStateMenu
                  current={node.state}
                  onSetState={onSetState}
                  onClose={() => setStateMenuOpen(false)}
                  // #953 — when the notes layer is on, a Blocked reason typed
                  // here persists as a note on this node, mirroring the drawer.
                  // Off → the prop is undefined and the reason step is a calm
                  // placeholder (commit Blocked, no note), exactly as today.
                  {...(onSaveBlockedReason ? { onSaveReason: onSaveBlockedReason } : {})}
                />
              )}
            </div>
          </div>

          {/* Empty-branch decomposition nudge (#980) — when the focused node has
              NO sub-tasks, a calm one-liner teaches the hard part (breaking a
              thing into steps) right where it happens. Decomposition is not an
              intuitive skill, so the UI prompts it rather than assuming it. It
              points at the `+ sub-task` button below. Factual + calm; about the
              WORK, never a person (§A9). Self-hides once the node has children. */}
          {!hasChildren && (
            <p
              data-testid="decompose-nudge"
              className="text-[11px] leading-snug"
              style={{ color: c.muted }}
            >
              What are the steps to reach this? Add a sub-task for each — go as deep as you need.
            </p>
          )}

          {/* Action buttons. `+ sub-task` and `@ assign` are the two primary
              actions; `link →` appears only when the dependency layer is on
              (#896) and points at the drag-to-link handle; `⋯` holds the rest.
              `⋯` is pushed to the right edge so the row reads as a tidy bar.
              `whitespace-nowrap` keeps each label on one line at the card width
              (#894). */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              data-testid="add-subtask"
              onClick={onAddSubtask}
              className="whitespace-nowrap rounded border px-2 py-1 text-[11px] transition-colors hover:bg-slate-50"
              style={{ borderColor: c.softBorder, color: c.teal }}
            >
              + sub-task
            </button>
            <div className="relative">
              <button
                type="button"
                aria-label="Assign people"
                data-testid="assign-button"
                onClick={() => setPickerOpen((v) => !v)}
                className="whitespace-nowrap rounded border px-2 py-1 text-[11px] transition-colors hover:bg-slate-50"
                style={{ borderColor: c.softBorder, color: c.slate }}
              >
                @ assign
              </button>
              {pickerOpen && (
                <div className="absolute left-0 top-full z-30 mt-1">
                  <AssignPopover
                    nodeTitle={node.title}
                    members={members}
                    assignedIds={node.assigneeIds}
                    onToggle={onAssignToggle}
                    onClose={() => setPickerOpen(false)}
                  />
                </div>
              )}
            </div>
            {/* `link →` (#896 + #904): present ONLY when the dependency layer
                is on. It now ARMS click-to-link mode — click it, then click the
                task this one comes before (the click-only path that complements
                the drag-the-→-port path, §A7). When armed it reads as the active
                control ("linking… ✕" — click again to cancel). When the layer is
                off there is no button at all (no misleading, dead control). The
                `stopPropagation` keeps the click off the card's focus handler. */}
            {dependenciesEnabled && (
              <button
                type="button"
                data-testid="link-button"
                aria-label={linkArmed ? "Cancel linking" : "Link to another task"}
                aria-pressed={linkArmed}
                title={
                  linkArmed
                    ? "Click a task to link to it — or click here again to cancel"
                    : "Click, then pick the task this one comes before — or drag the → port"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  // Toggle the arm: arming uses this card as the source; clicking
                  // again (armed) cancels. The canvas owns the actual arm state.
                  if (onArmLink) onArmLink();
                  else onSelect();
                }}
                className="whitespace-nowrap rounded border px-2 py-1 text-[11px] transition-colors hover:bg-slate-50"
                style={
                  linkArmed
                    ? { borderColor: c.teal, backgroundColor: c.teal, color: "#FFFFFF" }
                    : { borderColor: c.softBorder, color: c.teal }
                }
              >
                {linkArmed ? "linking… ✕" : "link →"}
              </button>
            )}
            {/* The card-level ◀/▶ "arrange for viewing" controls (#961) were
                removed in round-4 item ② (#1043) — they cluttered the focused
                row + the founder found them low-value next to drag-reparent and
                the upcoming freeform-link work (#1043 item ⑥). The pure helpers
                `siblingSwap`/`canReorderSibling` (`outline/outlineModel.ts`) +
                their tests remain so a future outline-side reorder can pick
                them up. */}
            <div className="relative ml-auto">
              <button
                type="button"
                aria-label="More actions"
                data-testid="overflow-button"
                onClick={() =>
                  setOverflowOpen((v) => {
                    // Closing the menu drops any half-open Delete confirm, so it
                    // never re-opens mid-confirm.
                    if (v) setConfirmingDelete(false);
                    return !v;
                  })
                }
                className="rounded border px-2 py-1 text-[11px] leading-none transition-colors hover:bg-slate-50"
                style={{ borderColor: c.softBorder, color: c.muted }}
              >
                ⋯
              </button>
              {overflowOpen && (
                <div
                  className={`absolute right-0 z-20 mt-1 rounded-md border bg-white p-1 shadow-lg ${
                    // The editorial Delete confirm needs room to name the
                    // sub-task impact; a plain menu stays compact.
                    editorial && confirmingDelete ? "w-52" : "w-32"
                  }`}
                  style={{ borderColor: c.softBorder }}
                  role="menu"
                >
                  <button
                    type="button"
                    data-testid="overflow-details"
                    onClick={() => {
                      onOpenDrawer();
                      setOverflowOpen(false);
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
                    style={{ color: c.ink }}
                  >
                    Open details
                  </button>
                  <button
                    type="button"
                    data-testid="overflow-rename"
                    onClick={() => {
                      setDraft(node.title);
                      setRenaming(true);
                      setOverflowOpen(false);
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
                    style={{ color: c.ink }}
                  >
                    Rename
                  </button>
                  {/* Round-4 ⑥ (#1043) — "Move under…" replaces the old
                      drag-a-card-onto-another reparent gesture (plain card
                      drag is now drag-to-pin). The parent opens its own
                      picker; the menu item is hidden for `link_block` cards
                      (they're off-tree and never get a parent). */}
                  {onMoveUnder && node.kind !== "link_block" && (
                    <button
                      type="button"
                      data-testid="overflow-move-under"
                      onClick={() => {
                        onMoveUnder();
                        setOverflowOpen(false);
                      }}
                      className="block w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
                      style={{ color: c.ink }}
                    >
                      Move under…
                    </button>
                  )}
                  {/* Round-4 ⑥ (#1043) — "Reset to auto layout" appears only
                      when this card is pinned (its `layoutX`/`layoutY` are
                      set). Clears both back to NULL so the tidy-tree takes
                      over again. Calm copy; never alarm. */}
                  {onResetAutoLayout && node.layoutX != null && node.layoutY != null && (
                    <button
                      type="button"
                      data-testid="overflow-reset-auto-layout"
                      onClick={() => {
                        onResetAutoLayout();
                        setOverflowOpen(false);
                      }}
                      className="block w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
                      style={{ color: c.ink }}
                    >
                      Reset to auto layout
                    </button>
                  )}
                  {/* Delete / Archive. The action soft-archives the node
                      (recoverable; history kept — §A6), so the copy stays calm
                      and reversible, never alarm-red.

                      EDITORIAL (#1043): a proper "Delete" affordance with a
                      one-step confirm so a stray tap never removes a task. The
                      read layer re-roots an archived parent's children onto the
                      canvas (it doesn't cascade — backend `archive_node` touches
                      one node), so Delete is offered for LEAF nodes only; a
                      parent shows a calm hint to clear its sub-tasks first
                      (never an orphaning delete). A cascade-archive endpoint is
                      the right way to allow deleting a whole branch (follow-up).

                      LEGACY: byte-identical to before — a single immediate
                      "Archive" item, no confirm, no leaf gating. */}
                  {editorial ? (
                    confirmingDelete ? (
                      <div
                        className="flex flex-col gap-1.5 px-2 py-1.5"
                        data-testid="overflow-delete-confirm-row"
                      >
                        <p className="text-[11px] leading-snug" style={{ color: c.slate }}>
                          {hasChildren ? (
                            <>
                              Remove its {node.totalDescendants} sub-task(s) first, then delete “
                              {node.title}”. You can bring anything back later — history is kept.
                            </>
                          ) : (
                            <>
                              Delete “{node.title}”? You can bring it back later — its history is
                              kept.
                            </>
                          )}
                        </p>
                        <div className="flex items-center gap-1.5">
                          {!hasChildren && (
                            <button
                              type="button"
                              data-testid="overflow-delete-confirm"
                              onClick={() => {
                                onArchive();
                                setConfirmingDelete(false);
                                setOverflowOpen(false);
                              }}
                              className="rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors hover:bg-amber-50"
                              style={{
                                borderColor:
                                  theme.stateStyle("blocked").dot ?? theme.stateStyle("blocked").fg,
                                color: theme.stateStyle("blocked").fg,
                              }}
                            >
                              Delete
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid="overflow-delete-cancel"
                            onClick={() => setConfirmingDelete(false)}
                            className="rounded px-2 py-0.5 text-[11px] transition-colors hover:bg-black/5"
                            style={{ color: c.muted }}
                          >
                            {hasChildren ? "OK" : "Keep"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        data-testid="overflow-delete"
                        onClick={() => setConfirmingDelete(true)}
                        className="block w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
                        style={{ color: c.muted }}
                      >
                        Delete
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      data-testid="overflow-archive"
                      onClick={() => {
                        onArchive();
                        setOverflowOpen(false);
                      }}
                      className="block w-full rounded px-2 py-1 text-left text-xs transition-colors hover:bg-slate-50"
                      style={{ color: c.muted }}
                    >
                      Archive
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
